/* eslint-disable @next/next/no-img-element */
import React, { useState, useMemo } from "react";
import ChevronUpSvg from "../../../../icons/ChevronUpSvg";
import CaretDown from "../../../../icons/CaretDown";
import { getWatermarkedImgixUrl } from "services";

interface PDPropertyDataSectionProps {
  propertyData: any;
}

const PDPropertyDataSection: React.FC<PDPropertyDataSectionProps> = ({
  propertyData,
}) => {
  // State for toggling "Show More" for the address
  const [isAddressExpanded, setIsAddressExpanded] = useState(false);

  // Check if address text exceeds the limit
  const isTextExceeded = useMemo(
    () => propertyData?.address?.length > 232,
    [propertyData?.address],
  );

  const truncatedAddress = useMemo(
    () => `${propertyData?.address?.substring(0, 232)}` || "N/A",
    [propertyData?.address],
  );

  const fullAddress = useMemo(
    () => propertyData?.address || "N/A",
    [propertyData?.address],
  );

  const propertyDetails = useMemo(
    () => [
      {
        label: "Area",
        value: propertyData?.area ? `${propertyData?.area} sq. ft` : "N/A",
      },
      {
        label: "Configuration",
        value: propertyData?.configuration
          ? `${propertyData?.configuration}`
          : "N/A",
      },
      {
        label: "Address",
        value: isTextExceeded ? `${truncatedAddress}...` : fullAddress,
      },
      {
        label: "Property Age",
        value: propertyData?.ageOfProperty
          ? `${propertyData?.ageOfProperty} year old`
          : "N/A",
      },
      {
        label: "Total Floors",
        value: propertyData?.totalFloors
          ? `${propertyData?.totalFloors} floors`
          : "N/A",
      },
      {
        label: "Possession",
        value: propertyData?.possession_type
          ? propertyData.possession_type
          : "N/A",
      },
    ],
    [isAddressExpanded, fullAddress, truncatedAddress, propertyData],
  );

  // const applicationDetails = useMemo(() => [], [propertyData]);

  const SectionHeader = ({ title }: { title: string }) => (
    <div className="flex items-center space-x-2 lg:space-x-3 mt-5 lg:mt-0">
      <div className="uppercase whitespace-nowrap text-sm">{title}</div>
      <div className="bg-custom-gradient h-[1px] flex-grow"></div>
    </div>
  );

  return (
    <div className="lg:flex lg:mt-10">
      <div className="clip-path-mobileClipPolygonTopLeft hidden lg:block">
        <img
          src={
            propertyData?.posted_properties_raw_data?.propertyPhotos?.length > 0
              ? getWatermarkedImgixUrl(
                  `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${propertyData?.posted_properties_raw_data?.propertyPhotos[0].id}`,
                )
              : "/../newPropertyImage.png"
          }
          alt="property image"
          className="w-full lg:max-w-[414px] lg:min-w-[340px] xl:min-w-[380px] h-[344px] lg:max-h-[350px] object-cover"
        />
      </div>
      <div className="mt-10 lg:mt-0 bg-brand-lightOrange py-8 lg:py-6 px-4 lg:px-6 w-full">
        {/* Property Details Section */}
        <SectionHeader title="Property Details" />
        <div className="font-openSans space-y-8 lg:space-y-0 mt-5 lg:mt-3 lg:grid lg:grid-cols-2 lg:gap-y-5 lg:gap-x-10 xl:gap-x-28 lg:mb-4 relative">
          {propertyDetails.map((item, index) => (
            <div key={index} className="flex lg:flex-col capitalize">
              <div className="min-w-[80px] whitespace-nowrap text-xs font-semibold pr-2">
                {item.label}
              </div>
              <div className="flex items-center">
                <div className="text-[10px] lg:text-xs lg:mt-[6px] break-all">
                  {item.value}
                </div>
                {item.label === "Address" && isTextExceeded && (
                  <div
                    className="p-1 ml-1 lg:ml-2 -mr-0 lg:-mr-6 bg-[#faf2f0] rounded-full shadow-lg border border-brand-borderGrey cursor-pointer"
                    onClick={() => setIsAddressExpanded((prev) => !prev)}
                  >
                    {!isAddressExpanded && <CaretDown />}
                  </div>
                )}
              </div>
            </div>
          ))}
          {isAddressExpanded && (
            <div className="absolute -top-24 lg:-top-0 z-10 left-0 bg-white p-2 xl:p-4 w-full rounded-md font-openSans flex items-center">
              <div className="text-[9px] xl:text-xs capitalize leading-4 tracking-wide break-words">
                {fullAddress}
              </div>
              <div
                className="p-1 xl:p-2 ml-2 rounded-full border border-brand-borderGrey shadow-md cursor-pointer"
                onClick={() => setIsAddressExpanded((prev) => !prev)}
              >
                <ChevronUpSvg width={16} height={16} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDPropertyDataSection;
